import React, { useEffect, useState, useContext } from 'react';
import './Sidebar.css';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { RefreshContext } from '../../../App';
import {
    IoChevronBack, IoChevronForward, IoGridOutline, IoLogOutOutline,
    IoChevronDown, IoPeopleOutline, IoPersonAddOutline, IoCalendarOutline,
    IoCheckmarkDoneCircleOutline, IoWalkOutline, IoWalletOutline, IoBusinessOutline,
} from 'react-icons/io5';
import { API_URL } from '../../../config';

const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';
const ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR = process.env.REACT_APP_ZSAAS_LOGIN_URL || process.env.REACT_APP_ZTOOL_URL;

const Sidebar = ({ refreshKey }) => {
    useLocation();
    const { triggerRefresh } = useContext(RefreshContext);

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [userDetails, setUserDetails] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(null);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [companyName, setCompanyName] = useState('My App');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(USER_DETAILS_KEY);
        if (stored) {
            try {
                setUserDetails(JSON.parse(stored));
            } catch (e) {
                console.error('Sidebar: Error parsing user details', e);
            }
        }
    }, [refreshKey]);

    useEffect(() => {
        const fetchIdentity = async () => {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            if (!token) return;
            try {
                setIsLoading(true);
                const headers = { Authorization: `Bearer ${token}` };

                const [companyRes, meRes] = await Promise.all([
                    fetch(`${API_URL}/company`, { headers }),
                    fetch(`${API_URL}/auth/me`, { headers }),
                ]);

                if (companyRes.ok) {
                    const company = await companyRes.json();
                    if (company.company_name) setCompanyName(company.company_name);
                }

                if (meRes.ok) {
                    const me = await meRes.json();
                    const u = me.user || {};
                    const role = (u.role || userDetails?.role?.name || 'USER').toString().toUpperCase();
                    setCurrentUserData({
                        role,
                        department: '—',
                        designation: '—',
                        id: u.id,
                    });
                    // Enrich with employee profile when possible
                    if (u.id) {
                        const empRes = await fetch(`${API_URL}/hr/employees/${u.id}`, { headers });
                        if (empRes.ok) {
                            const emp = await empRes.json();
                            setCurrentUserData({
                                role: (emp.role_name || role).toString().toUpperCase(),
                                department: emp.department_name || 'N/A',
                                designation: emp.designation_name || 'N/A',
                                id: u.id,
                            });
                        }
                    }
                } else if (userDetails) {
                    setCurrentUserData({
                        role: (userDetails.role?.name || 'USER').toString().toUpperCase(),
                        department: 'N/A',
                        designation: 'N/A',
                    });
                }
            } catch (error) {
                console.error('Sidebar: Error fetching identity', error);
                setCurrentUserData({
                    role: (userDetails?.role?.name || 'USER').toString().toUpperCase(),
                    department: 'N/A',
                    designation: 'N/A',
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchIdentity();
    }, [userDetails, refreshKey]);

    const toggleSidebarCollapse = () => {
        setIsCollapsed(!isCollapsed);
        if (!isCollapsed) setIsProfileOpen(false);
    };

    const toggleProfileDropdown = (e) => {
        e.stopPropagation();
        if (!isCollapsed) setIsProfileOpen(!isProfileOpen);
    };

    const handleLogout = () => {
        localStorage.clear();
        const mode = (process.env.REACT_APP_AUTH_MODE || 'standalone').toLowerCase();
        if (mode === 'platform' && ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) {
            window.location.replace(ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR);
        } else {
            window.location.href = '/login';
        }
        if (typeof triggerRefresh === 'function') triggerRefresh();
    };

    const avatarInitial = userDetails?.name ? userDetails.name.charAt(0).toUpperCase() : 'U';
    const menuItems = [{ id: 'dashboard', icon: <IoGridOutline />, text: 'Dashboard', path: '/dashboard' }];
    const userRole = (currentUserData?.role || userDetails?.role?.name || 'EMPLOYEE').toUpperCase();
    const isManagement = ['SUPER_ADMIN', 'ADMIN', 'HR'].includes(userRole);

    const hrmsItems = [
        ...(isManagement ? [{ id: 'employees', icon: <IoPeopleOutline />, text: 'Employees', path: '/employees' }] : []),
        { id: 'onboarding', icon: <IoPersonAddOutline />, text: 'Onboarding', path: '/onboarding' },
        { id: 'offboarding-exit', icon: <IoLogOutOutline />, text: 'Offboarding', path: '/offboarding-exit' },
        { id: 'holidays', icon: <IoCalendarOutline />, text: 'Holidays', path: '/holidays' },
        { id: 'attendance', icon: <IoCheckmarkDoneCircleOutline />, text: 'Attendance', path: '/attendance' },
        { id: 'leaves', icon: <IoWalkOutline />, text: 'Leaves Management', path: '/leaves' },
        { id: 'payroll', icon: <IoWalletOutline />, text: 'Payroll', path: '/payroll' },
        ...(isManagement ? [{ id: 'org-settings', icon: <IoBusinessOutline />, text: 'Org Settings', path: '/org-settings' }] : []),
    ];

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
                <Link to="/" className="logo-link">
                    <div className="logo">
                        <span className="logo-initials">{companyName.charAt(0)}</span>
                        {!isCollapsed && <span className="logo-name-text">{companyName}</span>}
                    </div>
                </Link>
                <div onClick={toggleSidebarCollapse} className="collapse-icon-wrapper">
                    {isCollapsed ? <IoChevronForward className="collapse-icon" /> : <IoChevronBack className="collapse-icon" />}
                </div>
            </div>

            <div className="sidebar-content">
                <div className="menu-group">
                    {!isCollapsed && <p className="menu-group-title">MAIN</p>}
                    <ul>{menuItems.map((item) => (
                        <li key={item.id}>
                            <NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}>
                                <span className="menu-item-icon">{item.icon}</span>
                                {!isCollapsed && <span className="menu-item-text">{item.text}</span>}
                            </NavLink>
                        </li>
                    ))}</ul>
                </div>

                <div className="menu-group">
                    {!isCollapsed && <p className="menu-group-title">HRMS</p>}
                    <ul>{hrmsItems.map((item) => (
                        <li key={item.id}>
                            <NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}>
                                <span className="menu-item-icon">{item.icon}</span>
                                {!isCollapsed && <span className="menu-item-text">{item.text}</span>}
                            </NavLink>
                        </li>
                    ))}</ul>
                </div>
            </div>

            <div className="sidebar-footer">
                <div className="user-profile-section" onClick={toggleProfileDropdown}>
                    <div className="user-profile-header-row">
                        <div className="avatar">{avatarInitial}</div>
                        {!isCollapsed && (
                            <>
                                <div className="user-header-info">
                                    <span className="user-name">{userDetails?.name || 'User'}</span>
                                    <span className="view-profile-text">View Profile</span>
                                </div>
                                <IoChevronDown className={`profile-dropdown-icon ${isProfileOpen ? 'open' : ''}`} />
                            </>
                        )}
                    </div>

                    {!isCollapsed && (
                        <div className={`user-details-container ${isProfileOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
                            <div className="info-item">
                                <span className="info-label">Role</span>
                                <span className="info-value">{isLoading ? '...' : (currentUserData?.role || 'N/A')}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Dept</span>
                                <span className="info-value">{isLoading ? '...' : (currentUserData?.department || 'N/A')}</span>
                            </div>
                            <Link to="/settings/profile" className="full-settings-link">Go to full settings</Link>
                        </div>
                    )}
                </div>

                <div onClick={handleLogout} className="logout-section">
                    <span className="menu-item-icon"><IoLogOutOutline /></span>
                    {!isCollapsed && <span>Logout</span>}
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
